import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FileText, FolderOpen, Users, BarChart3, Settings, Laptop, Calendar, GraduationCap, Award, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  FileText,
  FolderOpen,
  Users,
  BarChart3,
  Settings,
  Laptop,
  Calendar,
  GraduationCap,
  Award
};

// Slug to service index mapping
const slugToIndexMap = {
  "claims-consulting": 0,
  "medical-records-consulting": 1,
  "insured-patients": 2,
  "data-analysis-consulting": 3,
  "admin-consulting": 4,
  "technical-consulting": 5,
  "events-management": 6,
  "cme-and-medical-staff-training": 7,
  "elite-services": 8
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t, services, isRTL } = useLanguage();
  
  // Get service by slug mapping
  const serviceIndex = slugToIndexMap[slug];
  const service = serviceIndex !== undefined ? services[serviceIndex] : null;

  if (!service) {
    return (
      <div className="min-height flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">{isRTL ? 'الخدمة غير موجودة' : 'Service Not Found'}</h2>
          <Link to="/services" className="text-cyan-500 hover:text-cyan-600">{t.services.backToServices}</Link>
        </div>
      </div>
    );
  }

  const iconNames = ['FileText', 'FolderOpen', 'Users', 'BarChart3', 'Settings', 'Laptop', 'Calendar', 'GraduationCap', 'Award'];
  const IconComponent = iconMap[iconNames[serviceIndex]];

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-80 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&q=80)' }}
      >
        <div className="absolute inset-0 bg-slate-900 bg-opacity-70 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-cyan-500 w-24 h-24 flex items-center justify-center mx-auto mb-4">
              <IconComponent size={48} className="text-white" />
            </div>
            <h1 className={`text-5xl font-bold text-white ${isRTL ? 'text-right' : ''}`}>
              {isRTL && service.titleArabic ? service.titleArabic : service.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/services"
              className={`inline-flex items-center gap-2 text-cyan-500 hover:text-cyan-600 mb-8 font-semibold ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <ArrowLeft size={20} />
              {t.services.backToServices}
            </Link>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <h2 className={`text-3xl font-bold text-slate-800 mb-6 ${isRTL ? 'text-right' : ''}`}>
                {t.services.aboutService}
              </h2>
              
              {/* Special formatting for Elite Services */}
              {slug === 'elite-services' ? (
                <div className={`text-slate-700 text-lg leading-relaxed mb-8 ${isRTL ? 'text-right' : ''}`}>
                  {(isRTL && service.fullDescriptionArabic ? service.fullDescriptionArabic : service.fullDescription)
                    .split('\n\n')
                    .map((paragraph, idx) => {
                      // Check if paragraph starts with emoji (indicating a title)
                      const startsWithEmoji = /^[\u{1F300}-\u{1F9FF}]/u.test(paragraph);
                      
                      if (startsWithEmoji) {
                        // Split emoji and title from rest
                        const lines = paragraph.split('\n');
                        const titleLine = lines[0];
                        const content = lines.slice(1).join('\n');
                        
                        return (
                          <div key={idx} className="mb-6">
                            <h3 className="text-2xl font-bold text-cyan-600 mb-3">
                              {titleLine}
                            </h3>
                            <p className="text-slate-700 leading-relaxed">{content}</p>
                          </div>
                        );
                      } else {
                        return <p key={idx} className="mb-4">{paragraph}</p>;
                      }
                    })
                  }
                </div>
              ) : (
                <p className={`text-slate-700 text-lg leading-relaxed mb-8 ${isRTL ? 'text-right' : ''}`} style={{ whiteSpace: 'pre-line' }}>
                  {isRTL && service.fullDescriptionArabic ? service.fullDescriptionArabic : service.fullDescription}
                </p>
              )}

              <div className="border-t border-slate-200 pt-8 mt-8">
                <h3 className="text-2xl font-bold text-slate-800 mb-6">{t.services.keyBenefits}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle2 className="text-cyan-500 flex-shrink-0 mt-1" size={24} />
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className="font-bold text-slate-800 mb-1">{t.services.expertGuidance}</h4>
                      <p className="text-slate-600 text-sm">{t.services.expertGuidanceDesc}</p>
                    </div>
                  </div>
                  <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle2 className="text-cyan-500 flex-shrink-0 mt-1" size={24} />
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className="font-bold text-slate-800 mb-1">{t.services.complianceAssurance}</h4>
                      <p className="text-slate-600 text-sm">{t.services.complianceAssuranceDesc}</p>
                    </div>
                  </div>
                  <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle2 className="text-cyan-500 flex-shrink-0 mt-1" size={24} />
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className="font-bold text-slate-800 mb-1">{t.services.improvedEfficiency}</h4>
                      <p className="text-slate-600 text-sm">{t.services.improvedEfficiencyDesc}</p>
                    </div>
                  </div>
                  <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle2 className="text-cyan-500 flex-shrink-0 mt-1" size={24} />
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className="font-bold text-slate-800 mb-1">{t.services.qualityEnhancement}</h4>
                      <p className="text-slate-600 text-sm">{t.services.qualityEnhancementDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-cyan-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-slate-800 mb-3">{t.services.getStarted}</h3>
                <p className="text-slate-700 mb-4">
                  {t.services.getStartedText} {service.title.toLowerCase()} {t.services.getStartedQuestion}
                </p>
                <Link 
                  to="/contact"
                  className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-semibold transition-colors"
                >
                  {t.services.contactUs}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
